import { useGlobalNavEnvironment } from "@/components/navigation/nav-link-environment";
import { getFeatureFlags } from "@/gen_src/proto/chalk/server/v1/flag-FeatureFlagService_connectquery";
import { CHALK_ENVIRONMENT_HEADER_NAME } from "@/utils/connect-query/connect-client";
import { useRouteInfo } from "@/utils/hooks/url/use-route-info";
import {
  ScopedEnvironmentFlagsContext,
  type ScopedEnvironmentFlags,
} from "@/utils/hooks/use-category-nav-enabled";
import { FlagCheckerV2Impl, useFeatureFlagOverridesFromImage } from "@/utils/hooks/use-flags-grpc";
import { callUnaryMethod, useTransport } from "@connectrpc/connect-query";
import { useQuery } from "@tanstack/react-query";
import { useSession } from "next-auth/react";
import React, { type PropsWithChildren, useMemo } from "react";

/**
 * On a page whose URL names no environment (a global settings page), fetches the live flags of the
 * environment its nav points into, so the category nav gate reads that environment's
 * `onboard_experience` rather than the team's flags or anything the browser remembered. A failed
 * fetch counts as loaded with no flags, which keeps the classic nav.
 */
export const ScopedEnvironmentFlagsProvider: React.FC<PropsWithChildren> = ({ children }) => {
  const session = useSession();
  const { environmentId, isReady } = useRouteInfo();
  const globalNavEnvironment = useGlobalNavEnvironment();
  const scopedEnvironmentId =
    isReady && environmentId == null ? globalNavEnvironment?.environmentId : undefined;
  const transport = useTransport();
  const overrides = useFeatureFlagOverridesFromImage();
  const flagsQuery = useQuery({
    queryKey: ["scoped-environment-feature-flags", scopedEnvironmentId],
    queryFn: ({ signal }) =>
      callUnaryMethod(
        transport,
        getFeatureFlags,
        {},
        // The URL names no environment, so the request names the one the nav points into.
        { signal, headers: { [CHALK_ENVIRONMENT_HEADER_NAME]: scopedEnvironmentId ?? "" } },
      ),
    enabled: scopedEnvironmentId != null && session.status === "authenticated",
  });

  const value = useMemo((): ScopedEnvironmentFlags | undefined => {
    if (scopedEnvironmentId == null) return undefined;
    const status = flagsQuery.error ? "error" : flagsQuery.data != null ? "loaded" : "loading";
    return {
      environmentId: scopedEnvironmentId,
      flags: new FlagCheckerV2Impl({ status, flags: flagsQuery.data?.flags }, overrides),
    };
  }, [flagsQuery.data, flagsQuery.error, overrides, scopedEnvironmentId]);

  return (
    <ScopedEnvironmentFlagsContext.Provider value={value}>
      {children}
    </ScopedEnvironmentFlagsContext.Provider>
  );
};
